/*
  Interactiviteit voor de FitTowel-productpagina: duimnagel-galerij,
  aantal-stepper en (indien aanwezig) variantkeuze. Alle drie zijn losse
  custom elements, in dezelfde stijl als ft-carousel.js en ft-header.js.
*/

if (!customElements.get('ft-product-gallery')) {
  customElements.define(
    'ft-product-gallery',
    class FtProductGallery extends HTMLElement {
      connectedCallback() {
        this.main = this.querySelector('[data-gallery-main]');
        this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
        if (!this.main || this.thumbs.length === 0) return;

        this.onThumbClick = (event) => {
          const thumb = event.currentTarget;
          this.setActive(thumb);
        };

        this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.onThumbClick));
      }

      setActive(thumb) {
        const full = thumb.getAttribute('data-full');
        const full2x = thumb.getAttribute('data-full-2x');
        if (!full) return;

        this.main.src = full;
        this.main.srcset = full2x ? `${full} 1x, ${full2x} 2x` : '';

        this.thumbs.forEach((t) => {
          const active = t === thumb;
          t.classList.toggle('is-active', active);
          if (active) {
            t.setAttribute('aria-current', 'true');
          } else {
            t.removeAttribute('aria-current');
          }
        });
      }
    }
  );
}

if (!customElements.get('ft-quantity')) {
  customElements.define(
    'ft-quantity',
    class FtQuantity extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-qty-input]');
        this.minus = this.querySelector('[data-qty-minus]');
        this.plus = this.querySelector('[data-qty-plus]');
        if (!this.input) return;

        this.onMinus = () => this.step(-1);
        this.onPlus = () => this.step(1);

        this.minus?.addEventListener('click', this.onMinus);
        this.plus?.addEventListener('click', this.onPlus);
      }

      step(direction) {
        const min = Number(this.input.min) || 1;
        const current = Number(this.input.value) || min;
        const next = Math.max(min, current + direction);
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  );
}

if (!customElements.get('ft-variant-picker')) {
  customElements.define(
    'ft-variant-picker',
    class FtVariantPicker extends HTMLElement {
      connectedCallback() {
        const script = this.querySelector('[data-variants]');
        if (!script) return;

        try {
          this.variants = JSON.parse(script.textContent);
        } catch (error) {
          this.variants = [];
        }

        this.form = this.closest('form');
        this.idInput = this.form?.querySelector('[data-variant-id-input]');
        this.submitButton = this.form?.querySelector('[data-add-to-cart]');
        this.priceEl = this.closest('.ft-pdp__info')?.querySelector('.ft-pdp__price');
        this.stockEl = this.closest('.ft-pdp__info')?.querySelector('.ft-pdp__stock');

        this.onChange = () => this.syncVariant();
        this.addEventListener('change', this.onChange);
      }

      get selectedOptions() {
        const inputs = [...this.querySelectorAll('input[type="radio"]:checked')];
        const options = [];
        inputs.forEach((input) => {
          const position = Number(input.dataset.optionPosition) - 1;
          options[position] = input.value;
        });
        return options;
      }

      syncVariant() {
        const selected = this.selectedOptions;
        const match = this.variants.find((variant) =>
          selected.every((value, index) => variant.options[index] === value)
        );

        if (!match) return;

        if (this.idInput) this.idInput.value = match.id;

        if (this.priceEl) {
          const formatted = this.formatMoney(match.price);
          const compareNode = this.priceEl.querySelector('.ft-pdp__price-compare');
          this.priceEl.firstChild.textContent = formatted + ' ';
          if (match.compare_at_price && match.compare_at_price > match.price) {
            const compareText = this.formatMoney(match.compare_at_price);
            if (compareNode) {
              compareNode.textContent = compareText;
            } else {
              const span = document.createElement('span');
              span.className = 'ft-pdp__price-compare';
              span.textContent = compareText;
              this.priceEl.appendChild(span);
            }
          } else if (compareNode) {
            compareNode.remove();
          }
        }

        if (this.stockEl) {
          // Pre-order staat op productniveau, niet per variant: als het label
          // aanwezig is, geldt het voor elke variant die te koop is. Zonder
          // deze tak sprong de melding bij het wisselen van kleur terug naar
          // 'Op voorraad'.
          const preorderLabel = this.stockEl.dataset.preorderLabel;
          const availableLabel = preorderLabel || this.stockEl.dataset.inStockLabel;

          this.stockEl.textContent = match.available
            ? availableLabel || this.stockEl.textContent
            : this.stockEl.dataset.soldOutLabel || this.stockEl.textContent;
          this.stockEl.classList.toggle('is-unavailable', !match.available);
          this.stockEl.classList.toggle('is-preorder', Boolean(preorderLabel) && match.available);
        }

        if (this.submitButton) {
          this.submitButton.disabled = !match.available;
        }
      }

      formatMoney(cents) {
        return (cents / 100).toLocaleString('nl-NL', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'EUR',
        });
      }
    }
  );
}
